import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MODULES, ModuleDefinition, resolveEffectiveModules, visibleModules } from '../config/modules';
import { useCurrentUser, useAllowedModules } from '../auth/AuthProvider';
import { LccRole } from '../auth/types';
import { MoreIcon } from '../icons';
import { BottomSheet } from './BottomSheet';

function defaultGetVisibleModules(role: LccRole, allowedModules: string[] | null): ModuleDefinition[] {
  return visibleModules(MODULES, resolveEffectiveModules(role, allowedModules), role);
}

/**
 * Barra de navegacion inferior fija (hasta 4 modulos + "Mas").
 * Las pantallas visibles dependen del Rol del usuario y de su override individual
 * (Configuracion > Usuarios > Pantallas) - ver shared/config/modules.ts.
 *
 * Recibe `getVisibleModules` para poder reutilizarse en OTRAS aplicaciones de
 * la Suite (ej. Gestion Humana, ver shared/config/hrModules.ts), cada una con
 * su propio catalogo de pantallas y sus propias reglas de Admin-only /
 * Asignables - por defecto usa las de Control Operativo, sin que ningun call
 * site existente tenga que cambiar.
 */
export function BottomNavigation({ getVisibleModules = defaultGetVisibleModules }: { getVisibleModules?: (role: LccRole, allowedModules: string[] | null) => ModuleDefinition[] }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const allowedModules = useAllowedModules();

  const visible = useMemo(() => getVisibleModules(user.role, allowedModules), [getVisibleModules, user.role, allowedModules]);
  const bottomNavModules = visible.filter((m) => m.inBottomNav);
  // "Mas" solo muestra modulos ya activos - los que dicen "Pronto" quedan
  // ocultos hasta que se desarrollen (con solo cambiar su status a 'active'
  // en shared/config/modules.ts, reaparecen aqui sin tocar este archivo).
  const moreSheetModules = visible.filter((m) => !m.inBottomNav && m.status === 'active');

  const isActive = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  return (
    <>
      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-ink-100 bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-stretch justify-between px-2">
          {bottomNavModules.map((mod) => {
            const Icon = mod.icon;
            const active = isActive(mod.path);
            return (
              <button
                key={mod.id}
                onClick={() => navigate(mod.path)}
                className={[
                  'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 transition-colors duration-200',
                  active ? 'text-brand-600' : 'text-ink-400 hover:text-ink-600',
                ].join(' ')}
              >
                <Icon width={22} height={22} />
                <span className="text-[11px] font-medium">{mod.label}</span>
              </button>
            );
          })}
          {moreSheetModules.length > 0 && (
            <button
              onClick={() => setMoreOpen(true)}
              className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 text-ink-400 hover:text-ink-600"
            >
              <MoreIcon width={22} height={22} />
              <span className="text-[11px] font-medium">Más</span>
            </button>
          )}
        </div>
      </nav>

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Más módulos">
        <div className="grid grid-cols-3 gap-3 pb-2 pt-2">
          {moreSheetModules.map((mod) => {
            const Icon = mod.icon;
            return (
              <button
                key={mod.id}
                onClick={() => {
                  setMoreOpen(false);
                  navigate(mod.path);
                }}
                className="flex flex-col items-center gap-2 rounded-2xl border border-ink-100 p-4 hover:bg-ink-50"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-light text-brand-600">
                  <Icon width={22} height={22} />
                </div>
                <span className="text-center text-xs font-medium text-ink-700">{mod.label}</span>
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}
